import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useMemo } from 'react'
import Box from 'src/components/Box/Box'
import NotificationSettingsGroupEntry, {
  NotificationSettingsGroup,
} from 'src/components/NotificationSettingsGroupEntry/NotificationSettingsGroupEntry'
import { useTranslate } from 'src/localization/Trns'
import { NotificationSettingListModel } from 'src/models/notificationSetting.models'
import { PatchNotificationSettingsRequest } from 'src/requests/patchNotificationSettings.request'
import { css } from 'styled-components'

interface NotificationSettingsBoxProps {
  className?: string
  notificationSettings: NotificationSettingListModel[]
  patchNotifications: (body: PatchNotificationSettingsRequest) => void
}

const RECEIVER_INDEX = 0
const CATEGORY_INDEX = 1
const EVENT_INDEX = 2
const CHANNEL_INDEX = 3

const path1ToIndexMap: Record<string, number> = {
  creditCard: 0,
  delegateCreditCard: 1,
  virtualCard: 2,
  transaction: 3,
  expenseReport: 4,
}

const NotificationSettingsBox: React.FC<NotificationSettingsBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const notificationSettingsGroups: NotificationSettingsGroup[] = useMemo(
    () =>
      props.notificationSettings
        .reduce(
          (
            acc: NotificationSettingsGroup[],
            curr,
          ): NotificationSettingsGroup[] => {
            const segments = curr.path.split('.')
            const receiver = segments[RECEIVER_INDEX]
            const category = segments[CATEGORY_INDEX]
            const event = segments[EVENT_INDEX]
            const channel = segments[CHANNEL_INDEX]

            const index = path1ToIndexMap[category]
            const newGroups = [...acc]
            const newGroup: NotificationSettingsGroup = {
              ...acc[index],
              cardholderSettings:
                receiver === 'cardholder'
                  ? {
                      ...(acc[index]?.cardholderSettings || {}),
                      [event]: {
                        ...(acc[index]?.cardholderSettings?.[event] || {}),
                        [channel]: curr,
                      },
                    }
                  : acc[index]?.cardholderSettings,
              recipientSettings:
                receiver === 'recipient'
                  ? {
                      ...(acc[index]?.recipientSettings || {}),
                      [event]: {
                        ...(acc[index]?.recipientSettings?.[event] || {}),
                        [channel]: curr,
                      },
                    }
                  : acc[index]?.recipientSettings,
              emailSelected:
                acc[index]?.emailSelected +
                (channel === 'email' && curr.enabled ? 1 : 0),
              emailTotal:
                acc[index]?.emailTotal + (channel === 'email' ? 1 : 0),
              pushSelected:
                acc[index]?.pushSelected +
                (channel === 'push' && curr.enabled ? 1 : 0),
              pushTotal: acc[index]?.pushTotal + (channel === 'push' ? 1 : 0),
            }
            newGroups[index] = newGroup
            return newGroups
          },
          [
            {
              label: translate('Accounts'),
              analyticsLabel: 'Accounts',
              emailSelected: 0,
              emailTotal: 0,
              pushSelected: 0,
              pushTotal: 0,
              stringLabel: 'NotificationsSettingsAccountsCheckbox',
            },
            {
              label: translate('Budgets'),
              analyticsLabel: 'Budgets',
              emailSelected: 0,
              emailTotal: 0,
              pushSelected: 0,
              pushTotal: 0,
              stringLabel: 'NotificationsSettingsBudgetsCheckbox',
            },
            {
              label: translate('Virtual Cards'),
              analyticsLabel: 'Virtual Cards',
              emailSelected: 0,
              emailTotal: 0,
              pushSelected: 0,
              pushTotal: 0,
              stringLabel: 'NotificationsSettingsVirtualCardsCheckbox',
            },
            {
              label: translate('Transactions'),
              analyticsLabel: 'Transactions',
              emailSelected: 0,
              emailTotal: 0,
              pushSelected: 0,
              pushTotal: 0,
              stringLabel: 'NotificationsSettingsTransactionsCheckbox',
            },
            {
              label: translate('Reimbursements'),
              analyticsLabel: 'Reimbursements',
              emailSelected: 0,
              emailTotal: 0,
              pushSelected: 0,
              pushTotal: 0,
              stringLabel: 'NotificationsSettingsOutOfPocketExpensesCheckbox',
            },
          ],
        )
        .filter((group) => group.emailTotal + group.pushTotal > 0),
    [props.notificationSettings],
  )

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        padding-bottom: 10px;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('Notification Settings')}
      </span>
      <div
        css={css`
          margin-top: 10px;
          display: flex;
          flex-direction: column;
        `}
      >
        {notificationSettingsGroups.map((group, i) => (
          <NotificationSettingsGroupEntry
            key={i}
            notificationSettingGroup={group}
            patchNotifications={props.patchNotifications}
          />
        ))}
      </div>
    </Box>
  )
}

export default NotificationSettingsBox
